import React, { useState, useEffect } from 'react';
import { X, MoveRight, Mail, MessageCircle, Ban, Send, Save, Loader2, ChevronDown, ChevronUp } from 'lucide-react';
import AiRichTextEditor from './AiRichTextEditor';
import VoorgesteldClientProposalPanel, { createDefaultVoorgesteldClientPayload } from './VoorgesteldClientProposalPanel';
import type { VoorgesteldClientPayload } from '../utils/portalProposalThread';
import type { CVData } from '../types';
import { supabase } from '../services/supabaseService';
import { getCandidateEmail } from '../utils/candidateContact';
import { createDraftMessage, sendDirectMessage } from '../services/microsoftGraphService';
import { VOORGESTELD_RESUME_BUNDLE_KEY, saveVoorgesteldMoveDraft, loadVoorgesteldMoveDraft, clearVoorgesteldMoveDraft, resumeBundleMatchesVacancyEvent } from '../utils/voorgesteldResumeNavigation';

function candidateRowToCvData(row: any, candidateId: string): CVData {
    const p = row?.ai_parsed_data || row?.parsed_cv || {};
    const name = row?.name || p.personal_info?.name || 'Kandidaat';
    const email = row?.email || p.email || p.personal_info?.email || '';
    const phone = row?.phone || p.phone || p.personal_info?.phone || '';
    const wh = Array.isArray(p.work_experience) ? p.work_experience : Array.isArray(p.timeline) ? p.timeline : [];
    const workHistory = wh.map((w: any) => ({
        company: w.company || '',
        role: w.role || w.title || '',
        period: w.period || w.year || '',
        description: w.description || w.summary || '',
    }));
    const ed = Array.isArray(p.education) ? p.education : [];
    const education = ed.map((e: any) => ({
        school: e.school || e.institute || '',
        degree: e.degree || e.field || '',
        year: e.year || '',
    }));
    return {
        candidateId,
        title: `${name} — CV`,
        language: 'nl',
        summary: p.summary || p.profiel || row?.cv_summary || '',
        personalInfo: {
            name,
            email,
            phone,
            location: p.location || p.personal_info?.location || '',
            linkedin: row?.linkedin_url || p.linkedin || '',
            photoUrl: row?.photo_url || row?.avatar_url || '',
        },
        workHistory,
        education,
        skills: Array.isArray(p.skills) ? p.skills : [],
        motivation: p.motivation || '',
        references: [],
        languages: [],
        courses: [],
        hobbies: [],
        otherInfo: '',
    };
}

interface Template {
    id: string;
    type: string;
    name: string;
    content: string;
}

interface MoveDetails {
    candidateId: string;
    candidateRealId?: string; // Often 'id' on the original kandidaat object
    candidateName: string;
    candidateEmail?: string;
    fromStage: string;
    toStage: string;
    vacancyTitle?: string;
    clientName?: string;
    /** Standaard e-mailadres opdrachtgever (uit vacaturedata), voor vooraf invullen klant-e-mail. */
    defaultClientEmail?: string;
    /** Sollicitatie-id (applications.id) — nodig voor klantportaal-publicatie. */
    applicationId?: string;
    vacancyId?: string;
    clientId?: string;
    matchScore?: number;
    /** Platte CV-/profieltekst voor AI (o.a. samenvatting & match in klantpaneel). */
    candidateCvSnippet?: string;
}

export type { VoorgesteldClientPayload } from '../utils/portalProposalThread';

export interface CandidateMoveModalProps {
    isOpen: boolean;
    onClose: () => void;
    moveDetails: MoveDetails | null;
    templates: any[];
    signature?: string | null;
    currentUser: any;
    rejectionReasons?: string[];
    /** Korte vacaturetekst (plat) voor match-paneel bij «Voorgesteld». */
    contextVacancySnippet?: string;
    onConfirm: (
        communicationChannel: 'email' | 'whatsapp' | 'log',
        selectedTemplateId: string,
        subject: string,
        messageContent: string,
        internalNote: string,
        newTargetStage: string,
        asDraft: boolean,
        rejectionReason?: string,
        cc?: string,
        bcc?: string,
        fromEmail?: string,
        voorgesteldClient?: VoorgesteldClientPayload | null,
        /** Door modal opgehaald of door parent doorgegeven e-mailadres kandidaat. */
        resolvedCandidateEmail?: string,
    ) => Promise<void>;
}

const CandidateMoveModal: React.FC<CandidateMoveModalProps> = ({
    isOpen,
    onClose,
    moveDetails,
    templates,
    signature,
    currentUser,
    rejectionReasons = ['Kandidaat teruggetrokken', 'Afgewezen door opdrachtgever', 'Opdracht ingetrokken', 'Overig'],
    contextVacancySnippet,
    onConfirm
}) => {
    const [communicationChannel, setCommunicationChannel] = useState<'email' | 'whatsapp' | 'log'>('email');
    const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
    const [moveSubject, setMoveSubject] = useState<string>('');
    const [moveMessage, setMoveMessage] = useState<string>('');
    const [internalNote, setInternalNote] = useState<string>('');
    const [selectedRejectionReason, setSelectedRejectionReason] = useState<string>('');
    const [isMoving, setIsMoving] = useState(false);
    const [targetStage, setTargetStage] = useState<string>('');
    const [showSignature, setShowSignature] = useState(true);
    const [showInternalNote, setShowInternalNote] = useState(false);
    const [showShortcodes, setShowShortcodes] = useState(false);
    
    const [cc, setCc] = useState('');
    const [bcc, setBcc] = useState('');
    const [sharedMailboxes, setSharedMailboxes] = useState<string[]>([]);
    const [selectedFromEmail, setSelectedFromEmail] = useState<string>('');
    const [displayCandidateEmail, setDisplayCandidateEmail] = useState('');
    const [voorgesteldClient, setVoorgesteldClient] = useState<VoorgesteldClientPayload>(() => createDefaultVoorgesteldClientPayload());
    const [clientMailBusy, setClientMailBusy] = useState(false);

    const handleOpenDocumentBuilder = async () => {
        const cid = moveDetails?.candidateRealId || moveDetails?.candidateId;
        if (!moveDetails || !cid) {
            window.dispatchEvent(
                new CustomEvent('wt:toast', { detail: { message: 'Geen kandidaat gekoppeld.', type: 'error' } })
            );
            return;
        }
        try {
            const { data, error } = await supabase.from('candidates').select('*').eq('id', cid).maybeSingle();
            if (error) throw error;
            if (!data) {
                window.dispatchEvent(
                    new CustomEvent('wt:toast', { detail: { message: 'Kandidaat niet gevonden.', type: 'error' } })
                );
                return;
            }
            const cvData = candidateRowToCvData(data, cid);
            const appId = String(moveDetails.applicationId || moveDetails.candidateId || '');
            const vacTitle = String(moveDetails.vacancyTitle || '').trim() || 'Vacature';
            const docTitle = `Voorstel — ${moveDetails.candidateName} — ${vacTitle}`;
            try {
                sessionStorage.setItem(
                    VOORGESTELD_RESUME_BUNDLE_KEY,
                    JSON.stringify({
                        move: { ...moveDetails },
                        voorgesteld: voorgesteldClient,
                        targetStage,
                    })
                );
            } catch (e) {
                console.warn('[CandidateMoveModal] resume bundle', e);
            }
            window.dispatchEvent(
                new CustomEvent('wt:navigate', {
                    detail: {
                        tab: 'documentbuilder',
                        docContent: {
                            title: docTitle,
                            content: JSON.stringify({ _isDocumentBuilder: true, resumeData: cvData }),
                            candidateId: cid,
                            vacancyId: String(moveDetails.vacancyId || '').trim() || undefined,
                            vacancyTitle: vacTitle,
                        },
                        documentBuilderReturn: {
                            tab: 'vacancies',
                            vacancyId: moveDetails.vacancyId,
                            applicationId: appId,
                            portalProposal: true,
                        },
                    },
                })
            );
        } catch (e) {
            console.error(e);
            window.dispatchEvent(
                new CustomEvent('wt:toast', { detail: { message: 'Documentbuilder openen mislukt.', type: 'error' } })
            );
        }
    };

    const handleClientEmailM365 = async (mode: 'draft' | 'send') => {
        if (!voorgesteldClient.emailToClient) {
            window.dispatchEvent(
                new CustomEvent('wt:toast', {
                    detail: { message: 'Zet «E-mail naar klantcontact» aan om te mailen.', type: 'error' },
                })
            );
            return;
        }
        const rawTo = (voorgesteldClient.clientEmailTo || '').split(',').map((s) => s.trim()).filter(Boolean);
        const primary = rawTo[0];
        if (!primary) {
            window.dispatchEvent(
                new CustomEvent('wt:toast', { detail: { message: 'Geen geldig klant-e-mailadres ingevuld.', type: 'error' } })
            );
            return;
        }
        let personalizedBody = (voorgesteldClient.clientEmailBodyHtml || '').replace(/\n/g, '<br>');
        const clientSig = signature || '';
        const isSignatureInBody =
            clientSig &&
            personalizedBody.replace(/\s/g, '').includes(clientSig.replace(/\s/g, '').substring(0, 20));
        if (personalizedBody.match(/\[HANDTEKENING\]/i)) {
            personalizedBody = personalizedBody.replace(/\[HANDTEKENING\]/gi, clientSig);
        } else if (clientSig && !isSignatureInBody) {
            personalizedBody += `<br><br>${clientSig}`;
        }
        const ccFromPrimaryField = rawTo.slice(1);
        const ccFromPayload = (voorgesteldClient.clientEmailCc || '')
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean);
        const ccArr = [...ccFromPrimaryField, ...ccFromPayload, ...(cc ? cc.split(',').map((s) => s.trim()).filter(Boolean) : [])];
        const bccFromPayload = (voorgesteldClient.clientEmailBcc || '')
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean);
        const bccArr = [...bccFromPayload, ...(bcc ? bcc.split(',').map((s) => s.trim()).filter(Boolean) : [])];

        setClientMailBusy(true);
        try {
            if (mode === 'draft') {
                const { success, error } = await createDraftMessage(
                    voorgesteldClient.clientEmailSubject || 'Voorstel',
                    personalizedBody,
                    primary,
                    ccArr,
                    bccArr,
                    selectedFromEmail || undefined
                );
                if (!success) throw error;
                window.dispatchEvent(
                    new CustomEvent('wt:toast', { detail: { message: 'Concept in Outlook (M365) gezet.', type: 'success' } })
                );
            } else {
                const { success, error } = await sendDirectMessage(
                    voorgesteldClient.clientEmailSubject || 'Voorstel',
                    personalizedBody,
                    primary,
                    ccArr,
                    bccArr,
                    selectedFromEmail || undefined
                );
                if (!success) {
                    if (error?.message?.includes('does not have the right to send mail')) {
                        window.dispatchEvent(
                            new CustomEvent('wt:toast', {
                                detail: {
                                    message: `Geen Send As-recht voor ${selectedFromEmail || 'deze mailbox'}. Kies je eigen mailbox.`,
                                    type: 'error',
                                },
                            })
                        );
                    } else {
                        window.dispatchEvent(
                            new CustomEvent('wt:toast', { detail: { message: 'E-mail naar klant mislukt.', type: 'error' } })
                        );
                    }
                    return;
                }
                window.dispatchEvent(
                    new CustomEvent('wt:toast', { detail: { message: 'E-mail naar klant verzonden.', type: 'success' } })
                );
            }
        } catch (e) {
            console.error(e);
            window.dispatchEvent(
                new CustomEvent('wt:toast', { detail: { message: 'M365-actie mislukt.', type: 'error' } })
            );
        } finally {
            setClientMailBusy(false);
        }
    };

    /** Concept lokaal bewaren (teksten, klantportaal-vinkjes, gekoppeld document) — per sollicitatie. */
    const persistVoorstelConcept = () => {
        const aid = String(moveDetails?.applicationId || moveDetails?.candidateId || '').trim();
        if (!aid) {
            window.dispatchEvent(
                new CustomEvent('wt:toast', { detail: { message: 'Geen sollicitatie-id om op te slaan.', type: 'error' } }),
            );
            return;
        }
        saveVoorgesteldMoveDraft(aid, {
            voorgesteldClient,
            communicationChannel,
            selectedTemplateId,
            moveSubject,
            moveMessage,
            internalNote,
            cc,
            bcc,
            selectedFromEmail,
            targetStage,
        });
        window.dispatchEvent(
            new CustomEvent('wt:toast', { detail: { message: 'Voorstel opgeslagen op dit apparaat.', type: 'success' } }),
        );
    };

    // Reset state when modal opens (of hervat na Documentbuilder via sessionStorage-bundle)
    useEffect(() => {
        if (!isOpen || !moveDetails) return;

        let bundleHandled = false;
        try {
            const braw = sessionStorage.getItem(VOORGESTELD_RESUME_BUNDLE_KEY);
            if (braw) {
                const b = JSON.parse(braw) as {
                    move?: typeof moveDetails;
                    voorgesteld?: Partial<VoorgesteldClientPayload>;
                    targetStage?: string;
                };
                const mid = String(moveDetails.applicationId || moveDetails.candidateId || moveDetails.candidateRealId || '');
                if (b?.move && resumeBundleMatchesVacancyEvent(b, { vacancyId: moveDetails.vacancyId, applicationId: mid })) {
                    sessionStorage.removeItem(VOORGESTELD_RESUME_BUNDLE_KEY);
                    bundleHandled = true;
                    setCommunicationChannel('email');
                    setSelectedTemplateId('');
                    setMoveSubject('');
                    setMoveMessage('');
                    setInternalNote('');
                    setSelectedRejectionReason('');
                    setIsMoving(false);
                    setCc('');
                    setBcc('');
                    setSelectedFromEmail('');
                    const next = createDefaultVoorgesteldClientPayload(b.voorgesteld as VoorgesteldClientPayload);
                    try {
                        const rawCv = sessionStorage.getItem('wt_portal_proposal_cv');
                        if (rawCv) next.portalCvSnapshot = JSON.parse(rawCv) as Record<string, unknown>;
                    } catch {
                        /* ignore */
                    }
                    setVoorgesteldClient(next);
                    setTargetStage(b.targetStage || b.move?.toStage || 'Voorgesteld');
                }
            }
        } catch {
            /* ignore */
        }

        if (!bundleHandled) {
            setCommunicationChannel('email');
            setSelectedTemplateId('');
            setMoveSubject('');
            setMoveMessage('');
            setInternalNote('');
            setSelectedRejectionReason('');
            setIsMoving(false);
            setCc('');
            setBcc('');
            setSelectedFromEmail('');

            const draftAid = String(moveDetails?.applicationId || moveDetails?.candidateId || '').trim();
            const draft = draftAid ? loadVoorgesteldMoveDraft(draftAid) : null;

            const defaults = createDefaultVoorgesteldClientPayload({
                clientEmailSubject: moveDetails?.vacancyTitle
                    ? `Voorstel: ${moveDetails.candidateName} — ${moveDetails.vacancyTitle}`
                    : `Voorstel: ${moveDetails?.candidateName || ''}`,
                clientEmailTo: (moveDetails?.defaultClientEmail || '').trim(),
                portalMatchScore:
                    typeof moveDetails?.matchScore === 'number' && !Number.isNaN(moveDetails.matchScore)
                        ? Math.max(0, Math.min(100, Math.round(moveDetails.matchScore)))
                        : 0,
            });
            const mergedVoorgesteld = draft?.voorgesteld
                ? createDefaultVoorgesteldClientPayload({ ...defaults, ...draft.voorgesteld })
                : defaults;
            try {
                const rawCv = sessionStorage.getItem('wt_portal_proposal_cv');
                if (rawCv) mergedVoorgesteld.portalCvSnapshot = JSON.parse(rawCv) as Record<string, unknown>;
            } catch {
                /* ignore */
            }
            setVoorgesteldClient(mergedVoorgesteld);

            if (draft) {
                if (draft.communicationChannel === 'email' || draft.communicationChannel === 'whatsapp' || draft.communicationChannel === 'log') {
                    setCommunicationChannel(draft.communicationChannel);
                }
                if (typeof draft.selectedTemplateId === 'string') setSelectedTemplateId(draft.selectedTemplateId);
                if (typeof draft.moveSubject === 'string') setMoveSubject(draft.moveSubject);
                if (typeof draft.moveMessage === 'string') setMoveMessage(draft.moveMessage);
                if (typeof draft.internalNote === 'string') setInternalNote(draft.internalNote);
                if (typeof draft.cc === 'string') setCc(draft.cc);
                if (typeof draft.bcc === 'string') setBcc(draft.bcc);
                if (typeof draft.selectedFromEmail === 'string') setSelectedFromEmail(draft.selectedFromEmail);
                if (typeof draft.targetStage === 'string' && draft.targetStage.trim()) {
                    setTargetStage(draft.targetStage.trim());
                } else {
                    setTargetStage(moveDetails?.toStage || '');
                }
            } else {
                setTargetStage(moveDetails?.toStage || '');
            }
        }
    }, [isOpen, moveDetails]);

    useEffect(() => {
        if (!isOpen || !moveDetails) {
            setDisplayCandidateEmail('');
            return;
        }
        const fromMove = (moveDetails.candidateEmail || '').trim();
        if (fromMove) {
            setDisplayCandidateEmail(fromMove);
            return;
        }
        const cid = moveDetails.candidateRealId || moveDetails.candidateId;
        if (!cid) {
            setDisplayCandidateEmail('');
            return;
        }
        let cancelled = false;
        (async () => {
            const { data } = await supabase
                .from('candidates')
                .select('email, ai_parsed_data')
                .eq('id', cid)
                .maybeSingle();
            if (!cancelled) setDisplayCandidateEmail(getCandidateEmail(data));
        })();
        return () => {
            cancelled = true;
        };
    }, [isOpen, moveDetails?.candidateEmail, moveDetails?.candidateRealId, moveDetails?.candidateId]);

    useEffect(() => {
        if (isOpen && currentUser?.id && currentUser.id !== 'current-user') {
            import('../services/supabaseService').then(({ supabase }) => {
                supabase.from('profiles').select('client_id').eq('id', currentUser.id).single()
                    .then(({ data: profileData }) => {
                        if (profileData?.client_id) {
                            supabase.from('clients').select('data').eq('id', profileData.client_id).single()
                                .then(({ data }) => {
                                    if (data?.data?.shared_mailboxes) {
                                        setSharedMailboxes(data.data.shared_mailboxes.split(',').map((s: string) => s.trim()).filter(Boolean));
                                    }
                                    if (data?.data?.default_shared_mailbox) {
                                        setSelectedFromEmail(data.data.default_shared_mailbox);
                                    }
                                });
                        }
                    });
            });
        }
    }, [isOpen, currentUser]);

    useEffect(() => {
        const onPortalCv = () => {
            try {
                const raw = sessionStorage.getItem('wt_portal_proposal_cv');
                if (!raw) return;
                const snap = JSON.parse(raw) as Record<string, unknown>;
                setVoorgesteldClient((prev) => ({ ...prev, portalCvSnapshot: snap }));
            } catch {
                /* ignore */
            }
        };
        window.addEventListener('wt:portal-proposal-cv-saved', onPortalCv);
        return () => window.removeEventListener('wt:portal-proposal-cv-saved', onPortalCv);
    }, []);

    const handleTemplateSelect = (tid: string) => {
        setSelectedTemplateId(tid);
        const t = templates.find(t => t.id === tid);
        if (t && moveDetails) {
            const firstName = moveDetails.candidateName.split(' ')[0] || '';
            const lastName = moveDetails.candidateName.split(' ').slice(1).join(' ') || '';
            const vacancyTitle = moveDetails.vacancyTitle || 'de vacature';
            const clientName = moveDetails.clientName || 'onze opdrachtgever';

            const processTemplate = (text: string) => {
                return (text || '')
                    .replace(/\[name\]|\[naam\]|\[voornaam\]|\{\{name\}\}|\{\{naam\}\}/gi, firstName)
                    .replace(/\[achternaam\]|\{\{achternaam\}\}/gi, lastName)
                    .replace(/\[vacature\]|\[functie\]|\{\{vacature\}\}/gi, vacancyTitle)
                    .replace(/\[opdrachtgever\]|\[bedrijf\]|\[bedrij\]|\{\{opdrachtgever\}\}/gi, clientName)
                    .replace(/\[handtekening\]|\{\{handtekening\}\}/gi, signature || '');
            };

            setMoveMessage(processTemplate(t.body || t.content));
            setMoveSubject(processTemplate(t.subject));
        } else {
            setMoveMessage('');
            setMoveSubject('');
        }
    };

    const handleConfirmClick = async (asDraft: boolean = false) => {
        setIsMoving(true);
        try {
            const firstName = moveDetails.candidateName.split(' ')[0] || '';
            const lastName = moveDetails.candidateName.split(' ').slice(1).join(' ') || '';
            const vacancyTitle = moveDetails.vacancyTitle || 'de vacature';
            const clientName = moveDetails.clientName || 'onze opdrachtgever';

            const processedMessage = moveMessage
                .replace(/\[name\]|\[naam\]|\[voornaam\]|\{\{name\}\}|\{\{naam\}\}/gi, firstName)
                .replace(/\[achternaam\]|\{\{achternaam\}\}/gi, lastName)
                .replace(/\[vacature\]|\[functie\]|\{\{vacature\}\}/gi, vacancyTitle)
                .replace(/\[opdrachtgever\]|\[bedrijf\]|\[bedrij\]|\{\{opdrachtgever\}\}/gi, clientName)
                .replace(/\[handtekening\]|\{\{handtekening\}\}/gi, signature || '');
                
            const processedSubject = moveSubject
                .replace(/\[name\]|\[naam\]|\[voornaam\]|\{\{name\}\}|\{\{naam\}\}/gi, firstName)
                .replace(/\[achternaam\]|\{\{achternaam\}\}/gi, lastName)
                .replace(/\[vacature\]|\[functie\]|\{\{vacature\}\}/gi, vacancyTitle)
                .replace(/\[opdrachtgever\]|\[bedrijf\]|\[bedrij\]|\{\{opdrachtgever\}\}/gi, clientName);

            await onConfirm(
                communicationChannel, 
                selectedTemplateId, 
                processedSubject, 
                processedMessage, 
                internalNote, 
                targetStage, 
                asDraft, 
                targetStage === 'Afgewezen' ? selectedRejectionReason : undefined,
                cc,
                bcc,
                selectedFromEmail,
                targetStage === 'Voorgesteld' && !isClientPortal ? voorgesteldClient : null,
                displayCandidateEmail || moveDetails.candidateEmail || undefined,
            );
            const aid = String(moveDetails?.applicationId || moveDetails?.candidateId || '').trim();
            if (aid) clearVoorgesteldMoveDraft(aid);
        } finally {
            setIsMoving(false);
        }
    };

    if (!isOpen || !moveDetails) return null;

    const isClientPortal = window.location.hostname.toLowerCase().includes('clientportal') || new URLSearchParams(window.location.search).get('clientportal') === 'true';
    const isVoorgesteldWide = !isClientPortal && targetStage === 'Voorgesteld';

    const leftColumn = (
        <>
            <div className="flex shrink-0 items-center justify-between border-b border-slate-100 bg-white p-5 sm:px-6 sm:py-4">
                <div className="min-w-0 pr-2">
                    <h3 className="text-lg font-black uppercase tracking-tight text-slate-900 sm:text-xl">Kandidaat Status</h3>
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                        <span className="text-xs font-bold text-slate-500 sm:text-sm">{moveDetails.fromStage}</span>
                        <MoveRight size={16} className="shrink-0 text-slate-300" />
                        <select
                            value={targetStage}
                            onChange={(e) => {
                                if (isClientPortal) {
                                    window.dispatchEvent(
                                        new CustomEvent('wt:toast', {
                                            detail: { message: 'Deze optie is alleen voor Werfplaats recruiters.', type: 'error' },
                                        })
                                    );
                                    return;
                                }
                                setTargetStage(e.target.value);
                            }}
                            onClick={(e) => {
                                if (isClientPortal) {
                                    e.preventDefault();
                                    window.dispatchEvent(
                                        new CustomEvent('wt:toast', {
                                            detail: { message: 'Deze optie is alleen voor Werfplaats recruiters.', type: 'error' },
                                        })
                                    );
                                }
                            }}
                            className={`appearance-none rounded-lg border border-slate-200 px-2.5 py-1.5 text-[10px] font-black uppercase tracking-widest shadow-sm outline-none sm:px-3 sm:text-xs ${isClientPortal ? 'cursor-not-allowed bg-slate-100 text-slate-400' : 'cursor-pointer bg-red-50 text-[#e51b23] focus:border-red-300'}`}
                        >
                            <option value="Shortlist">SHORTLIST</option>
                            <option value="Voorgesteld">VOORGESTELD</option>
                            <option value="Gesprek">GESPREK</option>
                            <option value="Aanbod">AANBOD</option>
                            <option value="Plaatsing">PLAATSING</option>
                            <option value="Afgewezen">AFGEWEZEN</option>
                        </select>
                    </div>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                    {isVoorgesteldWide ? (
                        <button
                            type="button"
                            onClick={onClose}
                            className="rounded-xl p-2 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
                            aria-label="Sluiten"
                        >
                            <X className="h-5 w-5" />
                        </button>
                    ) : null}
                    <div className="relative">
                        <div className="flex h-12 w-12 items-center justify-center overflow-hidden rounded-full border border-slate-100 bg-slate-50 text-sm font-bold text-slate-900 shadow-sm sm:h-14 sm:w-14 sm:text-base">
                            {currentUser?.profile?.photo_url ? (
                                <img src={currentUser.profile.photo_url} alt="Recruiter" className="h-full w-full object-cover" />
                            ) : (
                                currentUser?.profile?.name?.charAt(0) || currentUser?.email?.charAt(0)?.toUpperCase() || 'R'
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {targetStage === 'Afgewezen' && (
                <div className="shrink-0 border-b border-red-100 bg-red-50 px-5 py-3 sm:px-6">
                    <label className="mb-2 block text-[10px] font-black uppercase tracking-widest text-red-600">Reden van Afwijzing</label>
                    <select
                        value={selectedRejectionReason}
                        onChange={(e) => setSelectedRejectionReason(e.target.value)}
                        disabled={isClientPortal}
                        className={`w-full rounded-lg border p-2.5 text-sm font-bold shadow-sm outline-none transition-all ${
                            isClientPortal
                                ? 'cursor-not-allowed border-slate-200 bg-slate-100 text-slate-400'
                                : 'border-red-200 bg-white text-red-900 focus:border-red-500 focus:ring-2 focus:ring-red-500/20'
                        }`}
                    >
                        <option value="">-- Selecteer een reden --</option>
                        {rejectionReasons.map((reason) => (
                            <option key={reason} value={reason}>
                                {reason}
                            </option>
                        ))}
                    </select>
                </div>
            )}

            <div className="custom-scrollbar min-h-0 flex-1 overflow-y-auto bg-[#f8fafc] p-4 sm:p-5">
                <div className="mb-4 rounded-xl border border-slate-100 bg-white p-4 shadow-sm">
                    <p className="mb-3 text-sm font-bold text-slate-700">
                        Wil je <span className="text-[#0073e6]">{moveDetails.candidateName}</span> een update sturen?
                    </p>
                    <div className="flex gap-2">
                        <button
                            type="button"
                            onClick={() => {
                                if (isClientPortal) {
                                    window.dispatchEvent(
                                        new CustomEvent('wt:toast', {
                                            detail: {
                                                message: 'Upgrade portal voor whatsapp of mailfuncties, vraag snel naar de mogelijkheden.',
                                                type: 'error',
                                            },
                                        })
                                    );
                                    return;
                                }
                                setCommunicationChannel('email');
                                setMoveMessage('');
                            }}
                            className={`flex flex-1 items-center justify-center gap-2 rounded-lg py-2 text-[10px] font-black uppercase tracking-widest transition-all ${
                                communicationChannel === 'email'
                                    ? 'border border-red-500 bg-red-50 text-red-600 shadow-sm ring-1 ring-red-500'
                                    : 'border bg-white text-slate-400 hover:border-slate-300'
                            } ${isClientPortal ? 'cursor-not-allowed opacity-50 blur-[1px]' : ''}`}
                        >
                            <Mail size={16} /> Email
                        </button>
                        <button
                            type="button"
                            onClick={() => {
                                if (isClientPortal) {
                                    window.dispatchEvent(
                                        new CustomEvent('wt:toast', {
                                            detail: {
                                                message: 'Upgrade portal voor whatsapp of mailfuncties, vraag snel naar de mogelijkheden.',
                                                type: 'error',
                                            },
                                        })
                                    );
                                    return;
                                }
                                setCommunicationChannel('whatsapp');
                                setMoveMessage('');
                            }}
                            className={`flex flex-1 items-center justify-center gap-2 rounded-lg py-2 text-[10px] font-black uppercase tracking-widest transition-all ${
                                communicationChannel === 'whatsapp'
                                    ? 'border border-green-500 bg-green-50 text-green-700 shadow-sm ring-1 ring-green-500'
                                    : 'border bg-white text-slate-400 hover:border-slate-300'
                            } ${isClientPortal ? 'cursor-not-allowed opacity-50 blur-[1px]' : ''}`}
                        >
                            <MessageCircle size={14} /> WhatsApp
                        </button>
                        <button
                            type="button"
                            onClick={() => {
                                setCommunicationChannel('log');
                                setMoveMessage('');
                            }}
                            className={`flex flex-1 items-center justify-center gap-2 rounded-lg py-2 text-[10px] font-black uppercase tracking-widest transition-all ${
                                communicationChannel === 'log'
                                    ? 'border border-slate-300 bg-slate-200 text-slate-700 shadow-inner'
                                    : 'border bg-white text-slate-400 hover:border-slate-300'
                            }`}
                        >
                            <Ban size={14} /> Log Only
                        </button>
                    </div>
                </div>

                {communicationChannel !== 'log' ? (
                    <div className="animate-fadeIn space-y-4 transition-opacity">
                        <div className="space-y-4 rounded-xl border border-slate-100 bg-white p-4 shadow-sm sm:p-5">
                            <div>
                                <label className="mb-2 block text-[10px] font-black uppercase tracking-widest text-slate-400">Selecteer Template</label>
                                <select
                                    value={selectedTemplateId}
                                    onChange={(e) => handleTemplateSelect(e.target.value)}
                                    className="w-full rounded-lg border border-slate-200 bg-slate-50 p-2.5 text-sm font-bold text-slate-800 outline-none transition-all focus:border-[#0073e6] focus:ring-2 focus:ring-[#0073e6]/20"
                                >
                                    <option value="">-- Geen template --</option>
                                    {templates
                                        .filter((t) => t.type !== 'Section')
                                        .map((t) => (
                                            <option key={t.id} value={t.id}>
                                                {t.name}
                                            </option>
                                        ))}
                                </select>
                            </div>

                            {communicationChannel === 'email' && (
                                <div className="space-y-4">
                                    {sharedMailboxes.length > 0 && (
                                        <div>
                                            <label className="mb-2 block text-[10px] font-black uppercase tracking-widest text-slate-400">
                                                Verzenden Als (Afzender)
                                            </label>
                                            <select
                                                value={selectedFromEmail}
                                                onChange={(e) => setSelectedFromEmail(e.target.value)}
                                                className="w-full cursor-pointer rounded-lg border border-slate-200 bg-slate-50 p-2.5 text-sm font-bold text-slate-800 outline-none transition-all focus:border-[#0073e6] focus:ring-2 focus:ring-[#0073e6]/20"
                                            >
                                                <option value="">-- Mijn eigen mailbox --</option>
                                                {sharedMailboxes.map((mb) => (
                                                    <option key={mb} value={mb}>
                                                        {mb}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    )}

                                    <div>
                                        <label className="mb-2 block text-[10px] font-black uppercase tracking-widest text-slate-400">Aan (Kandidaat)</label>
                                        <input
                                            type="text"
                                            value={displayCandidateEmail || 'Geen e-mailadres bekend'}
                                            readOnly
                                            className="w-full cursor-not-allowed rounded-lg border border-slate-200 bg-slate-50 p-2.5 text-sm font-bold text-slate-500 outline-none"
                                        />
                                    </div>

                                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                        <div>
                                            <label className="mb-2 block text-[10px] font-black uppercase tracking-widest text-slate-400">
                                                CC (komma gescheiden)
                                            </label>
                                            <input
                                                type="text"
                                                value={cc}
                                                onChange={(e) => setCc(e.target.value)}
                                                className="w-full rounded-lg border border-slate-200 bg-white p-2.5 text-sm font-medium text-slate-800 outline-none transition-all focus:border-[#0073e6] focus:ring-2 focus:ring-[#0073e6]/20"
                                                placeholder="bijv. collega@bedrijf.nl"
                                            />
                                        </div>
                                        <div>
                                            <label className="mb-2 block text-[10px] font-black uppercase tracking-widest text-slate-400">BCC</label>
                                            <input
                                                type="text"
                                                value={bcc}
                                                onChange={(e) => setBcc(e.target.value)}
                                                className="w-full rounded-lg border border-slate-200 bg-white p-2.5 text-sm font-medium text-slate-800 outline-none transition-all focus:border-[#0073e6] focus:ring-2 focus:ring-[#0073e6]/20"
                                                placeholder="bijv. archief@bedrijf.nl"
                                            />
                                        </div>
                                    </div>

                                    <div>
                                        <label className="mb-2 block text-[10px] font-black uppercase tracking-widest text-slate-400">Onderwerp</label>
                                        <input
                                            type="text"
                                            value={moveSubject}
                                            onChange={(e) => setMoveSubject(e.target.value)}
                                            className="w-full rounded-lg border border-slate-200 bg-white p-2.5 text-sm font-bold text-slate-800 outline-none transition-all placeholder:font-medium placeholder:text-slate-300 focus:border-[#0073e6] focus:ring-2 focus:ring-[#0073e6]/20"
                                            placeholder="Typ het onderwerp..."
                                        />
                                    </div>
                                </div>
                            )}

                            <div className="mt-4 border-t border-slate-100 pt-4">
                                <label className="mb-2 block text-[10px] font-black uppercase tracking-widest text-slate-400">Bericht aan Kandidaat</label>
                                <AiRichTextEditor
                                    value={moveMessage}
                                    onChange={setMoveMessage}
                                    placeholder="Typ je bericht hier..."
                                    minHeight={isVoorgesteldWide ? '120px' : '150px'}
                                    className="overflow-hidden rounded-lg border-slate-200"
                                />
                            </div>
                        </div>

                        <div className="space-y-2.5">
                            {communicationChannel === 'email' && signature && (
                                <div className="overflow-hidden rounded-xl border border-slate-100 bg-white shadow-sm">
                                    <button
                                        type="button"
                                        onClick={() => setShowSignature(!showSignature)}
                                        className="flex w-full items-center justify-between px-5 py-3 text-[10px] font-black uppercase tracking-widest text-slate-500 transition-colors hover:bg-slate-50 focus:outline-none"
                                    >
                                        <span>M365 Handtekening {showSignature ? '(Zichtbaar)' : '(Verborgen)'}</span>
                                        {showSignature ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                                    </button>
                                    {showSignature && (
                                        <div className="animate-fadeIn px-5 pb-5 pt-1">
                                            <div
                                                className="custom-scrollbar max-h-32 overflow-y-auto rounded-lg border border-slate-200 bg-slate-50 p-4 text-xs opacity-80"
                                                dangerouslySetInnerHTML={{ __html: signature }}
                                            />
                                        </div>
                                    )}
                                </div>
                            )}

                            <div className="overflow-hidden rounded-xl border border-slate-100 bg-white shadow-sm">
                                <button
                                    type="button"
                                    onClick={() => setShowShortcodes(!showShortcodes)}
                                    className="flex w-full items-center justify-between px-5 py-3 text-[10px] font-black uppercase tracking-widest text-slate-500 transition-colors hover:bg-slate-50 focus:outline-none"
                                >
                                    <span>Shortcodes toevoegen</span>
                                    {showShortcodes ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                                </button>
                                {showShortcodes && (
                                    <div className="flex flex-wrap gap-2 px-5 pb-5 pt-1 text-[10px] font-bold uppercase tracking-wider text-slate-500 animate-fadeIn">
                                        <span
                                            className="cursor-pointer rounded bg-slate-100 px-2 py-1 transition-colors hover:bg-slate-200"
                                            onClick={() => setMoveMessage((m) => m + '[naam]')}
                                        >
                                            [naam]
                                        </span>
                                        <span
                                            className="cursor-pointer rounded bg-slate-100 px-2 py-1 transition-colors hover:bg-slate-200"
                                            onClick={() => setMoveMessage((m) => m + '[achternaam]')}
                                        >
                                            [achternaam]
                                        </span>
                                        <span
                                            className="cursor-pointer rounded bg-slate-100 px-2 py-1 transition-colors hover:bg-slate-200"
                                            onClick={() => setMoveMessage((m) => m + '[vacature]')}
                                        >
                                            [vacature]
                                        </span>
                                        <span
                                            className="cursor-pointer rounded bg-slate-100 px-2 py-1 transition-colors hover:bg-slate-200"
                                            onClick={() => setMoveMessage((m) => m + '[opdrachtgever]')}
                                        >
                                            [opdrachtgever]
                                        </span>
                                        <span
                                            className="cursor-pointer rounded bg-slate-100 px-2 py-1 transition-colors hover:bg-slate-200"
                                            onClick={() => setMoveMessage((m) => m + '[handtekening]')}
                                        >
                                            [handtekening]
                                        </span>
                                    </div>
                                )}
                            </div>

                            <div className="overflow-hidden rounded-xl border border-slate-100 bg-white shadow-sm">
                                <button
                                    type="button"
                                    onClick={() => setShowInternalNote(!showInternalNote)}
                                    className="flex w-full items-center justify-between px-5 py-3 text-[10px] font-black uppercase tracking-widest text-slate-500 transition-colors hover:bg-slate-50 focus:outline-none"
                                >
                                    <span className="flex items-center gap-2">
                                        Interne Notitie (Optioneel){' '}
                                        {internalNote ? <span className="h-2 w-2 rounded-full bg-[#0073e6]" /> : null}
                                    </span>
                                    {showInternalNote ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                                </button>
                                {showInternalNote && (
                                    <div className="animate-fadeIn px-5 pb-5 pt-1">
                                        <textarea
                                            value={internalNote}
                                            onChange={(e) => setInternalNote(e.target.value)}
                                            className="h-20 w-full resize-none rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs font-medium outline-none focus:border-[#0073e6] focus:ring-2 focus:ring-[#0073e6]/20"
                                            placeholder="Typ hier een notitie voor je collega's..."
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="animate-fadeIn rounded-xl border border-slate-100 bg-white p-5 shadow-sm transition-opacity">
                        <label className="mb-3 block text-[10px] font-black uppercase tracking-widest text-slate-400">
                            Interne Notitie / Log Context
                        </label>
                        <textarea
                            value={internalNote}
                            onChange={(e) => setInternalNote(e.target.value)}
                            className="h-24 w-full resize-none rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs font-medium outline-none focus:ring-2 focus:ring-slate-300"
                            placeholder={`Kandidaat wordt verplaatst naar ${targetStage}... typ hier de verplichte/optionele notitie/log.`}
                        />
                    </div>
                )}
            </div>

            <div className="flex shrink-0 items-center gap-3 border-t border-slate-100 bg-white p-4 sm:px-6">
                <button
                    type="button"
                    onClick={onClose}
                    className="rounded-lg px-4 py-3 text-[10px] font-bold uppercase tracking-widest text-slate-500 transition-colors hover:bg-slate-50 sm:px-5 sm:py-3.5"
                >
                    Annuleren
                </button>
                <div className="flex flex-1 flex-wrap gap-2">
                    {communicationChannel === 'email' || communicationChannel === 'whatsapp' ? (
                        <>
                            <button
                                type="button"
                                onClick={() => void handleConfirmClick(true)}
                                disabled={
                                    isMoving ||
                                    !moveMessage.trim() ||
                                    (communicationChannel === 'email' && !moveSubject.trim()) ||
                                    (targetStage === 'Afgewezen' && !selectedRejectionReason)
                                }
                                className="flex min-w-[120px] flex-1 items-center justify-center gap-1.5 rounded-lg border-2 border-slate-200 bg-white py-3 text-[9px] font-black uppercase tracking-widest text-slate-600 shadow-sm transition-all hover:border-[#0078D4] hover:text-[#0078D4] disabled:opacity-50 disabled:hover:border-slate-200 disabled:hover:text-slate-600 sm:text-[10px]"
                            >
                                {isMoving ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />}
                                Concept Opslaan
                            </button>
                            <button
                                type="button"
                                onClick={() => void handleConfirmClick(false)}
                                disabled={
                                    isMoving ||
                                    !moveMessage.trim() ||
                                    (communicationChannel === 'email' && !moveSubject.trim()) ||
                                    (targetStage === 'Afgewezen' && !selectedRejectionReason)
                                }
                                className={`flex min-w-[120px] flex-1 items-center justify-center gap-1.5 rounded-lg py-3 text-[9px] font-black uppercase tracking-widest text-white shadow-lg transition-all disabled:opacity-50 sm:text-[10px] ${
                                    communicationChannel === 'whatsapp' ? 'bg-green-600 hover:bg-green-700' : 'bg-[#0078D4] hover:bg-[#0063B1]'
                                }`}
                            >
                                {isMoving ? <Loader2 className="animate-spin" size={14} /> : <Send size={14} />}
                                {communicationChannel === 'whatsapp' ? 'Direct WhatsApp' : 'Direct Verzenden'}
                            </button>
                        </>
                    ) : (
                        <button
                            type="button"
                            onClick={() => void handleConfirmClick(false)}
                            disabled={isMoving || (targetStage === 'Afgewezen' && !selectedRejectionReason)}
                            className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-slate-900 py-3.5 text-[10px] font-black uppercase tracking-widest text-white shadow-lg transition-all hover:bg-black disabled:opacity-50"
                        >
                            {isMoving ? <Loader2 className="animate-spin" size={14} /> : <Send size={14} />}
                            {communicationChannel !== 'log' ? 'Versturen & Verplaatsen' : isClientPortal ? 'Notitie Opslaan' : 'Verplaatsen / Loggen'}
                        </button>
                    )}
                </div>
            </div>
        </>
    );

    return (
        <div
            className={`fixed inset-0 z-[200] flex bg-slate-900/50 backdrop-blur-sm transition-opacity animate-in fade-in ${
                isVoorgesteldWide ? '' : 'justify-end'
            }`}
        >
            <div
                className={`flex bg-white shadow-2xl ${
                    isVoorgesteldWide
                        ? 'fixed inset-y-0 right-0 h-full w-[min(96vw,75vw)] max-w-[1600px] flex-row overflow-hidden border-l border-slate-200'
                        : 'h-full w-full max-w-[550px] flex-col animate-slideInRight duration-300'
                }`}
            >
                <div className={`flex min-h-0 flex-col ${isVoorgesteldWide ? 'w-[min(100%,38%)] shrink-0 border-r border-slate-100' : 'w-full'}`}>
                    {leftColumn}
                </div>
                {isVoorgesteldWide ? (
                    <div className="flex min-h-0 min-w-0 flex-1 flex-col bg-[#f8fafc]">
                        <div className="flex shrink-0 items-center justify-between border-b border-slate-200 bg-white px-4 py-3 sm:px-5">
                            <div>
                                <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Deel 2 — Klant</p>
                                <p className="text-sm font-bold text-slate-800">Voorstel in portaal en/of e-mail</p>
                            </div>
                            <button
                                type="button"
                                onClick={onClose}
                                className="rounded-full p-2 text-slate-400 transition hover:bg-slate-100 hover:text-slate-800"
                                aria-label="Sluiten"
                            >
                                <X size={20} />
                            </button>
                        </div>
                        <div className="custom-scrollbar min-h-0 flex-1 overflow-y-auto">
                            <VoorgesteldClientProposalPanel
                                vacancyTitle={moveDetails.vacancyTitle || 'Vacature'}
                                clientName={moveDetails.clientName || ''}
                                candidateName={moveDetails.candidateName}
                                matchScore={moveDetails.matchScore}
                                candidateCvSnippet={moveDetails.candidateCvSnippet}
                                vacancySnippet={contextVacancySnippet}
                                aiCandidateId={moveDetails.candidateRealId}
                                aiVacancyId={moveDetails.vacancyId}
                                aiApplicationId={moveDetails.applicationId || moveDetails.candidateId}
                                defaultClientEmail={moveDetails.defaultClientEmail}
                                clientEmailSignature={signature || null}
                                onOpenDocumentBuilder={handleOpenDocumentBuilder}
                                clientMailBusy={clientMailBusy}
                                clientSendFromLabel={selectedFromEmail || undefined}
                                onClientEmailDraft={() => void handleClientEmailM365('draft')}
                                onClientEmailSend={() => void handleClientEmailM365('send')}
                                value={voorgesteldClient}
                                onChange={setVoorgesteldClient}
                                onSavePortalDraft={isClientPortal ? undefined : persistVoorstelConcept}
                                recruiterUserId={currentUser?.id}
                            />
                        </div>
                    </div>
                ) : null}
            </div>
        </div>
    );
};

export default CandidateMoveModal;

